import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  CircularProgress,
  Checkbox,
  Button, Grid,Grow,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import axios from "axios";
import PropTypes from "prop-types";
import PlanVsActualOpeLog from "./PlanVsActualOpeLog";
import TableGenerate from "./tablegenerate";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function PlanVsActualStepper({ selectedDate, machineCode }) {
  const steps = ["Step 1: Shift-log Filtering", "Step 2: Operation-log Filtering", "Step 3: Review & Submit"];
  const [activeStep, setActiveStep] = useState(0);
  const [shiftLogs, setShiftLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedShiftRows, setSelectedShiftRows] = useState([]);
  const [shiftStartDatetime, setShiftStartDatetime] = useState(null);
  const [shiftOutDatetime, setShiftOutDatetime] = useState(null);
  const [selectedOperationRows, setSelectedOperationRows] = useState([]);
  const [checked, setChecked] = useState(false);

  const startTime = dayjs(selectedDate).hour(7).minute(30).second(0).format("YYYY-MM-DD HH:mm:ss");
  const endTime = dayjs(selectedDate).add(1, "day").hour(8).minute(0).second(0).format("YYYY-MM-DD HH:mm:ss");

  useEffect(() => {
    setChecked(true);
  }, []);

  useEffect(() => {
    if (activeStep === 0) {
      setLoading(true);
      setError("");
      axios
        .post(`${API_BASE_URL}/shiftlogForPlanVsActual`, { startTime, endTime, machineCode })
        .then((response) => {
          setShiftLogs(response.data);
          setSelectedShiftRows(response.data.map((_, index) => index + 1));
        })
        .catch((err) => {
          console.error("Error fetching shift logs:", err);
          setError("Failed to fetch shift logs. Please try again.");
        })
        .finally(() => setLoading(false));
    }
  }, [activeStep, startTime, endTime, machineCode]);

  useEffect(() => {
    if (selectedShiftRows.length > 0) {
      const selectedData = shiftLogs.filter((_, index) => selectedShiftRows.includes(index + 1));
      const minLoggedIn = selectedData
        .map((row) => dayjs(row.shift_loggedIn_DateTime))
        .reduce((min, curr) => (curr.isBefore(min) ? curr : min), dayjs(selectedDate).add(1, "day"));
      const maxLoggedOut = selectedData
        .map((row) =>
          row.shift_loggedOut_DateTime
            ? dayjs(row.shift_loggedOut_DateTime)
            : dayjs(selectedDate).add(1, "day").hour(8).minute(0).second(0)
        )
        .reduce((max, curr) => (curr.isAfter(max) ? curr : max), dayjs(selectedDate).hour(8).minute(0).second(0));
      setShiftStartDatetime(minLoggedIn.format("YYYY-MM-DD hh:mm:ss A"));
      setShiftOutDatetime(maxLoggedOut.format("YYYY-MM-DD hh:mm:ss A"));
    } else {
      setShiftStartDatetime(dayjs(selectedDate).hour(8).minute(0).second(0).format("YYYY-MM-DD hh:mm A"));
      setShiftOutDatetime(dayjs(selectedDate).add(1, "day").hour(8).minute(0).second(0).format("YYYY-MM-DD hh:mm A"));
    }
  }, [selectedShiftRows, shiftLogs, selectedDate]);

  const shiftColumns = [
    {
      field: "select",
      headerName: "Select",
      width: 100,
      renderCell: (params) => (
        <Checkbox
          checked={selectedShiftRows.includes(params.row.id)}
          onChange={(e) => {
            const newSelectedRows = [...selectedShiftRows];
            if (e.target.checked) {
              newSelectedRows.push(params.row.id);
            } else {
              const index = newSelectedRows.indexOf(params.row.id);
              if (index > -1) newSelectedRows.splice(index, 1);
            }
            setSelectedShiftRows(newSelectedRows);
          }}
        />
      ),
    },
    { field: "Employee_ID", headerName: "Employee ID", width: 150 },
    {
      field: "shift_loggedIn_DateTime",
      headerName: "Logged In",
      width: 200,
      renderCell: (params) => (params.value ? dayjs(params.value).format("YYYY-MM-DD hh:mm A") : "N/A"),
    },
    {
      field: "shift_loggedOut_DateTime",
      headerName: "Logged Out",
      width: 200,
      renderCell: (params) => (params.value ? dayjs(params.value).format("YYYY-MM-DD hh:mm A") : "N/A"),
    },
    { field: "operator", headerName: "Operator", width: 200 },
    { field: "AstOperator", headerName: "Assistant Operator", width: 200 },
    { field: "Helpers", headerName: "Helpers", width: 200 },
    { field: "ActiveStatus", headerName: "Status", width: 150 },
    { field: "remarks", headerName: "Remarks", width: 300 },
  ];

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  let stepOneContent;
  if (loading) {
    stepOneContent = <CircularProgress />;
  } else if (error) {
    stepOneContent = <Typography color="error">{error}</Typography>;
  } else {
    stepOneContent = (
      <Box sx={{ height: 400, width: '100%' }}>
        <DataGrid
          rows={shiftLogs.map((log, index) => ({ id: index + 1, ...log }))}
          columns={shiftColumns}
          pageSize={5}
          rowsPerPageOptions={[5, 10, 20]}
        />
      </Box>

    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ padding: "0px 0px 10px" }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Grow in={checked} timeout={1000}>
                <Card sx={{ backgroundColor: "#e3f2fd", borderRadius: "8px", boxShadow: 2 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: "bold", color: "#0d47a1" }}>
                      Summary Related Date
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#0d47a1" }}>
                      {dayjs(selectedDate).format("DD MMMM YYYY")}
                    </Typography>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Grow in={checked} timeout={1500}>
                <Card sx={{ backgroundColor: "#e3f2fd", borderRadius: "8px", boxShadow: 2 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: "bold", color: "#0d47a1" }}>
                      Work Station
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#0d47a1" }}>
                      {machineCode}
                    </Typography>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Grow in={checked} timeout={2000}>
                <Card sx={{ backgroundColor: "#e3f2fd", borderRadius: "8px", boxShadow: 2 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: "bold", color: "#0d47a1" }}>
                      From
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#0d47a1" }}>
                      {dayjs(shiftStartDatetime).format("DD MMM YYYY HH:mm:ss")}
                    </Typography>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Grow in={checked} timeout={2500}>
                <Card sx={{ backgroundColor: "#e3f2fd", borderRadius: "8px", boxShadow: 2 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: "bold", color: "#0d47a1" }}>
                      To
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#0d47a1" }}>
                      {dayjs(shiftOutDatetime).format("DD MMM YYYY HH:mm:ss")}
                    </Typography>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ width: "100%", mb: 2 }}>
          <Grow in={checked} timeout={1500}>
            <Stepper activeStep={activeStep}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Grow>
        </Box>

        {activeStep === 0 && <Box sx={{ mt: 2 }}>{stepOneContent}</Box>}

        {activeStep === 1 && (
          <PlanVsActualOpeLog
            shiftStartDatetime={shiftStartDatetime}
            shiftOutDatetime={shiftOutDatetime}
            machineCode={machineCode}
            setSelectedOperationLogs={setSelectedOperationRows}
          />
        )}

        {activeStep === 2 && (
          <TableGenerate
            selectedShiftRows={shiftLogs.filter((_, index) => selectedShiftRows.includes(index + 1))}
            selectedOperationRows={selectedOperationRows}
            shiftOutDatetime={shiftOutDatetime}
            machineCode={machineCode}
            shiftStartDatetime={shiftStartDatetime}
            selectedDate={selectedDate}
          />
        )}

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
          <Button onClick={handleBack} disabled={activeStep === 0}>
            Back
          </Button>
          <Button onClick={handleNext} disabled={activeStep === steps.length - 1}>
            Next
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

PlanVsActualStepper.propTypes = {
  selectedDate: PropTypes.instanceOf(Date).isRequired,
  machineCode: PropTypes.string.isRequired,
};
